/**
 * Consumer Wallet — Trust Requests inbox.
 *
 * Route: /wallet/requests/:consumer_id
 *
 * Shows every Trust Request targeted at this consumer across every
 * institution. Consumer can Accept, Decline or Complete inline (each
 * action calls /api/consumer/{cid}/trust-requests/{rid}/respond).
 *
 * NOTE: In production the wallet is device-scoped and resolves the
 * consumer_id from the PDV entry. For MVP this page accepts consumer_id
 * as a URL param so it can be surfaced from the wallet dashboard once
 * a consumer_id is known.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import axios from "axios";
import {
  KeyRound, Fingerprint, ShieldCheck, ClipboardCheck, BookOpen,
  GraduationCap, Package, CheckCircle2, XCircle, Send, Inbox,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = {
  identity_verification: { icon: Fingerprint,   label: "Identity Verification" },
  consent:               { icon: KeyRound,      label: "Consent" },
  kyc:                   { icon: ShieldCheck,   label: "KYC" },
  affordability:         { icon: ClipboardCheck,label: "Affordability" },
  product_understanding: { icon: BookOpen,      label: "Product Understanding" },
  learning_assignment:   { icon: GraduationCap, label: "Learning Assignment" },
  trust_package:         { icon: Package,       label: "Trust Package" },
};

const STATUS_TONE = {
  sent:                { label: "New",                 bg: "#DBEAFE", color: "#1E40AF" },
  consumer_accepted:   { label: "Accepted",            bg: "#E0E7FF", color: "#3730A3" },
  consumer_responding: { label: "You're responding",   bg: "#FEF3C7", color: "#92400E" },
  institution_review:  { label: "Awaiting institution",bg: "#CCFBF1", color: "#0F766E" },
  completed:           { label: "Completed",           bg: "#D1FAE5", color: "#065F46" },
  declined:            { label: "Declined",            bg: "#FEE2E2", color: "#7F1D1D" },
  expired:             { label: "Expired",             bg: "#FEE2E2", color: "#7F1D1D" },
  cancelled:           { label: "Cancelled",           bg: "#F1F5F9", color: "#475569" },
};

const ACTIVE = new Set(["sent", "consumer_accepted", "consumer_responding", "institution_review"]);
const TERMINAL = new Set(["completed", "declined", "expired", "cancelled", "failed"]);

export default function WalletTrustRequestsPage() {
  const { consumer_id } = useParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/consumer/${consumer_id}/trust-requests`,
      );
      setRequests(data);
    } catch (e) {
      toast.error("Failed to load your Trust Requests");
    } finally {
      setLoading(false);
    }
  }, [consumer_id]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => ({
    pending:   requests.filter((r) => ACTIVE.has(r.status)),
    completed: requests.filter((r) => r.status === "completed"),
    expired:   requests.filter((r) => r.status === "expired"),
    history:   requests.filter((r) => TERMINAL.has(r.status)),
  }), [requests]);

  async function respond(rid, decision, outcome = {}) {
    setBusy((b) => ({ ...b, [rid]: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/consumer/${consumer_id}/trust-requests/${rid}/respond`,
        { decision, outcome },
      );
      toast.success(decision === "accept" ? "Accepted" : decision === "decline" ? "Declined" : "Completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy((b) => ({ ...b, [rid]: false }));
    }
  }

  const visible = grouped[tab] || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="wallet-trust-requests">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto p-4 space-y-1">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
            Consumer Wallet · Trust Requests
          </div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>
            Your Trust Requests
          </h1>
          <p className="text-[12px] text-slate-500">
            Institutions ask you for information here — nothing happens without your consent.
          </p>
        </div>
        <nav className="max-w-2xl mx-auto flex gap-1 px-4" data-testid="wallet-request-tabs">
          {[
            { k: "pending",   l: "Pending",   badge: grouped.pending.length   },
            { k: "completed", l: "Completed", badge: grouped.completed.length },
            { k: "expired",   l: "Expired",   badge: grouped.expired.length   },
            { k: "history",   l: "History",   badge: grouped.history.length   },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`wallet-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`text-[12px] font-semibold px-3 py-2 transition ${
                tab === t.k
                  ? "border-b-2 text-slate-900"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              style={tab === t.k ? { borderColor: GOLD } : {}}
            >
              {t.l}{t.badge > 0 ? ` · ${t.badge}` : ""}
            </button>
          ))}
        </nav>
      </header>

      {/* List */}
      <main className="max-w-2xl mx-auto p-4 space-y-3">
        {loading && <div className="text-sm text-slate-500 text-center py-8">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
            <Inbox className="w-6 h-6 text-slate-300 mx-auto" />
            <div className="text-[14px] font-semibold text-slate-600 mt-2">No requests here yet</div>
            <div className="text-[12px] text-slate-500 mt-1">When an institution asks you for information, it appears in this inbox.</div>
          </div>
        )}
        {!loading && visible.map((r) => (
          <ConsumerRequestCard
            key={r.request_id}
            req={r}
            busy={busy[r.request_id]}
            onAccept={() => respond(r.request_id, "accept")}
            onDecline={() => respond(r.request_id, "decline")}
            onComplete={() => respond(r.request_id, "complete", { source: "wallet_mvp" })}
          />
        ))}
      </main>
    </div>
  );
}

function ConsumerRequestCard({ req, busy, onAccept, onDecline, onComplete }) {
  const type = REQUEST_TYPES[req.request_type] || { icon: Send, label: req.request_type };
  const Icon = type.icon;
  const tone = STATUS_TONE[req.status] || STATUS_TONE.sent;
  const requestedAgo = req.created_at
    ? formatDistanceToNow(new Date(req.created_at), { addSuffix: true })
    : "";
  const canAccept   = req.status === "sent";
  const canRespond  = req.status === "consumer_accepted" || req.status === "consumer_responding";
  const canDecline  = ACTIVE.has(req.status);
  return (
    <article
      data-testid={`wallet-request-card-${req.request_id}`}
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}22`, color: GOLD }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{req.purpose}</div>
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {type.label} · Requested {requestedAgo}
          </div>
          {req.purpose_description && (
            <p className="text-[13px] text-slate-700 leading-relaxed mt-2">
              {req.purpose_description}
            </p>
          )}
          {req.requested_information?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {req.requested_information.map((ri) => (
                <span key={ri} className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#334155" }}>
                  {ri.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {(canAccept || canRespond || canDecline) && (
        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          {canAccept && (
            <button data-testid={`wallet-accept-${req.request_id}`} disabled={busy} onClick={onAccept}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}>
              <CheckCircle2 className="w-4 h-4" /> Accept
            </button>
          )}
          {canRespond && (
            <button data-testid={`wallet-complete-${req.request_id}`} disabled={busy} onClick={onComplete}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50" style={{ background: "#0F766E" }}>
              <CheckCircle2 className="w-4 h-4" /> Submit
            </button>
          )}
          {canDecline && (
            <button data-testid={`wallet-decline-${req.request_id}`} disabled={busy} onClick={onDecline}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-50">
              <XCircle className="w-4 h-4" /> Decline
            </button>
          )}
        </div>
      )}
    </article>
  );
}
